import React from "react";

import { AirtimeProvider } from "../../../../features/auth/store/types/airtime-types";
import { MdOutlineDelete } from "react-icons/md";
import { FiEdit3 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchAirtimeProviders } from "../../../../features/auth/store/airtimeProviderSlice";

interface Props {
  providers: AirtimeProvider[];
  loading: boolean;
  onDelete: (id: number) => void;
  onEdit: (id: number) => void;
}

const AirtimeProvidersTable: React.FC<Props> = ({
  providers,
  loading,
  onDelete,
  onEdit,
}) => {
  const {
    pagination,
    lastPage: totalPages,
    currentPage,
  } = useSelector((state: RootState) => state.airtimeProviders);
  const dispatch = useDispatch<AppDispatch>();
  // Calculate pagination values
  const { nextPageUrl, prevPageUrl } = pagination;

  // Handle page navigation
  const goToNextPage = () => {
    if (currentPage < totalPages && nextPageUrl !== null) {
      dispatch(fetchAirtimeProviders({ url: nextPageUrl }));
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1 && prevPageUrl !== null) {
      dispatch(fetchAirtimeProviders({ url: prevPageUrl }));
    }
  };
  if (loading)
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  if (!providers.length) return <div>No providers found.</div>;

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full border border-[#FFFFFF1A] rounded-2xl overflow-hidden font-Inter">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="px-6 text-left">Name</th>
              <th className="px-6 text-left">Description</th>
              <th className="px-6 text-left">Status</th>
              <th className="px-6 text-left">Image</th>
              <th className="px-6 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
            {providers.map((provider) => (
              <tr
                key={provider.id}
                className="border-b border-gray-200 h-[50px]"
              >
                <td className="px-6 py-4 align-middle">{provider.name}</td>
                <td className="px-6 py-4 align-middle">
                  {provider.description}
                </td>
                <td className="px-6 py-4 align-middle">
                  <span
                    className={
                      provider.status === "on"
                        ? "text-green-600"
                        : "text-red-600"
                    }
                  >
                    {provider.status === "on" ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 py-4 align-middle">
                  {provider.image_url ? (
                    <img
                      src={provider.image_url}
                      alt={provider.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-6 py-4 align-middle flex items-center gap-2">
                  <button
                    className="text-blue-600"
                    onClick={() => onEdit(provider.id)}
                  >
                    <FiEdit3 className="text-2xl" />
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => onDelete(provider.id)}
                  >
                    <MdOutlineDelete className="text-2xl" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center border-t border-[#FFFFFF1A]">
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === totalPages || totalPages === 0
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          onClick={goToNextPage}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AirtimeProvidersTable;
